import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  FileEdit,
  CheckCircle2,
  XCircle,
  UserPlus,
  LifeBuoy,
  MessageSquare,
  ListChecks,
} from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";

// Per-type visual treatment. Bold + coloured so the kind of event is obvious.
const TYPE_META = {
  change_request: { icon: FileEdit, ring: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
  request_approved: { icon: CheckCircle2, ring: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
  request_rejected: { icon: XCircle, ring: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300" },
  connection: { icon: UserPlus, ring: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
  support_reply: { icon: LifeBuoy, ring: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300" },
  message: { icon: MessageSquare, ring: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
  task: { icon: ListChecks, ring: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
  task_done: { icon: CheckCircle2, ring: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
};

function timeAgo(iso) {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// `tone` = "light" (on light bars) | "dark" (on dark/coloured bars)
export default function NotificationBell({
  enabled = true,
  tone = "light",
  allHref = "/dashboard/notifications",
}) {
  const { items, unread, markRead, markAllRead } = useNotifications(enabled);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // The bell surfaces ONLY unread items; the full history lives on the page.
  const visible = items.filter((n) => !n.is_read);

  const onItem = (n) => {
    if (!n.is_read) markRead(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const openAll = () => {
    setOpen(false);
    navigate(allHref);
  };

  const btnTone =
    tone === "dark"
      ? "text-white/90 hover:bg-white/15"
      : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800";

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`relative grid h-9 w-9 place-items-center rounded-lg transition ${btnTone}`}
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell className="h-[18px] w-[18px]" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 px-1 place-items-center rounded-full bg-rose-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-slate-900">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-2 w-[22rem] max-w-[92vw] rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden">
            {/* HEADER */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
              <div className="font-semibold text-slate-900 dark:text-white">
                Notifications
                {unread > 0 && (
                  <span className="ml-2 text-xs font-normal text-slate-400">
                    {unread} unread
                  </span>
                )}
              </div>
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </button>
              )}
            </div>

            {/* LIST */}
            <div className="max-h-[60vh] overflow-y-auto">
              {visible.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <Bell className="mx-auto h-7 w-7 text-slate-300 dark:text-slate-600" />
                  <p className="mt-2 text-sm text-slate-400">
                    You're all caught up.
                  </p>
                </div>
              ) : (
                visible.map((n) => {
                  const meta = TYPE_META[n.type] || {
                    icon: Bell,
                    ring: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
                  };
                  const Icon = meta.icon;
                  return (
                    <button
                      key={n.id}
                      onClick={() => onItem(n)}
                      className={`w-full flex gap-3 px-4 py-3 text-left border-b border-slate-100 dark:border-slate-800 transition hover:bg-slate-50 dark:hover:bg-slate-800/60 ${
                        n.is_read ? "" : "bg-emerald-50/40 dark:bg-emerald-500/[0.06]"
                      }`}
                    >
                      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${meta.ring}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="block text-sm font-semibold text-slate-900 dark:text-white truncate">
                            {n.title}
                          </span>
                          {!n.is_read && (
                            <span className="h-2 w-2 shrink-0 rounded-full bg-rose-500" />
                          )}
                        </span>
                        {n.body && (
                          <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                            {n.body}
                          </span>
                        )}
                        <span className="mt-0.5 block text-[11px] text-slate-400">
                          {timeAgo(n.created_at)}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            {/* FOOTER */}
            <button
              onClick={openAll}
              className="w-full border-t border-slate-200 dark:border-slate-800 px-4 py-3 text-center text-xs font-semibold text-emerald-600 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
            >
              View all notifications
            </button>
          </div>
        </>
      )}
    </div>
  );
}
